import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../config';
import './WalletPage.css';

const TABS = ['Aperçu', 'Dépôt', 'Retrait', 'Dépôts', 'Vérification'];

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const MAX_SIZE       = 10 * 1024 * 1024; // 10 MB

const DOC_TYPES = [
  { id: 'identity', label: "Pièce d'identité", icon: '🪪', desc: 'CNI recto-verso ou passeport' },
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }) {
  const map = {
    pending:  { label: '⏳ En attente', cls: 'status-pending'  },
    approved: { label: '✓ Approuvé',   cls: 'status-approved' },
    rejected: { label: '✗ Refusé',     cls: 'status-rejected' },
  };
  const s = map[status] || { label: status, cls: '' };
  return <span className={`deposit-status-badge ${s.cls}`}>{s.label}</span>;
}

/* ══════════════════════════════════════════════════
   DEPOSIT TAB — M-Pesa flow
══════════════════════════════════════════════════ */
function DepositTab({ accessToken, onDepositSuccess }) {
  const fileRef = useRef(null);

  const [amount, setAmount]       = useState('');
  const [reference, setReference] = useState('');
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [step, setStep]           = useState('form'); // form | uploading | submitting | done | error
  const [errorMsg, setErrorMsg]   = useState('');
  const [deposit, setDeposit]     = useState(null);

  const reset = () => {
    setAmount(''); setReference(''); setFile(null); setPreview(null);
    setStep('form'); setErrorMsg(''); setDeposit(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type)) { setErrorMsg('Format non accepté. Utilisez JPEG, PNG, WebP, HEIC ou HEIF.'); return; }
    if (f.size > MAX_SIZE) { setErrorMsg('Fichier trop grand (max 10 Mo).'); return; }
    setErrorMsg('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    // Frontend validations
    if (!amount || parseFloat(amount) <= 0) { setErrorMsg('Le montant doit être supérieur à 0.'); return; }
    if (!reference.trim()) { setErrorMsg('La référence M-Pesa est obligatoire.'); return; }
    if (!file) { setErrorMsg('Veuillez sélectionner votre capture M-Pesa.'); return; }

    setErrorMsg('');

    try {
      // Step 1 — upload image
      setStep('uploading');
      const formData = new FormData();
      formData.append('file', file);

      const uploadRes = await fetch(`${API_BASE_URL}/api/v1/uploads/image`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.detail || `Upload échoué (${uploadRes.status})`);
      }

      const { url: proofUrl } = await uploadRes.json();

      // Step 2 — create deposit
      setStep('submitting');
      const depositRes = await fetch(`${API_BASE_URL}/api/v1/deposits`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          amount:      parseFloat(amount),
          reference:   reference.trim(),
          proof_image: proofUrl,
        }),
      });

      if (!depositRes.ok) {
        const err = await depositRes.json().catch(() => ({}));
        throw new Error(err.detail || `Dépôt échoué (${depositRes.status})`);
      }

      const data = await depositRes.json();
      setDeposit(data);
      setStep('done');
      onDepositSuccess?.();

    } catch (err) {
      setErrorMsg(err.message || 'Une erreur est survenue.');
      setStep('error');
    }
  };

  if (step === 'done' && deposit) {
    return (
      <div className="wallet-section">
        <div className="wallet-card deposit-success-card">
          <div className="dep-success-icon">✅</div>
          <h3 className="dep-success-title">Dépôt soumis avec succès !</h3>
          <p className="dep-success-desc">
            Votre dépôt de <strong>{parseFloat(deposit.amount).toLocaleString('fr-FR')} FC</strong> est en attente de validation par notre équipe.
            Vous serez notifié une fois approuvé.
          </p>
          <div className="dep-success-details">
            <div className="dsd-row">
              <span>Référence</span>
              <strong>{deposit.reference}</strong>
            </div>
            <div className="dsd-row">
              <span>Statut</span>
              <StatusBadge status={deposit.status} />
            </div>
            <div className="dsd-row">
              <span>Date</span>
              <span>{formatDate(deposit.created_at)}</span>
            </div>
            {deposit.proof_image && (
              <div className="dsd-row">
                <span>Preuve</span>
                <a href={`${API_BASE_URL}${deposit.proof_image}`} target="_blank" rel="noreferrer" className="dsd-proof-link">
                  Voir l'image →
                </a>
              </div>
            )}
          </div>
          <button className="wha-btn primary" onClick={reset}>Nouveau dépôt</button>
        </div>
      </div>
    );
  }

  const isLoading = step === 'uploading' || step === 'submitting';

  return (
    <div className="wallet-section">
      <div className="wallet-card">
        <h3 className="wc-title">Dépôt via M-Pesa</h3>
        <p className="wc-desc">Envoyez votre paiement M-Pesa, puis remplissez ce formulaire avec la référence et une capture d'écran.</p>

        {/* Amount */}
        <div className="dep-field">
          <label className="dep-label">Montant déposé (€)</label>
          <input
            type="number"
            className="dep-input"
            placeholder="Ex : 100"
            min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* Reference */}
        <div className="dep-field">
          <label className="dep-label">Référence M-Pesa</label>
          <input
            type="text"
            className="dep-input"
            placeholder="Ex : QHG7X3K9P2"
            value={reference}
            onChange={(e) => setReference(e.target.value.toUpperCase())}
            disabled={isLoading}
          />
          <p className="dep-hint">La référence se trouve dans le SMS de confirmation M-Pesa.</p>
        </div>

        {/* Image upload */}
        <div className="dep-field">
          <label className="dep-label">Capture d'écran M-Pesa</label>

          {!file ? (
            <div
              className="invoice-dropzone"
              onClick={() => fileRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className="idz-icon">📱</div>
              <p className="idz-main">Glissez ou cliquez pour ajouter votre capture</p>
              <p className="idz-sub">JPEG, PNG, WebP, HEIC · Max 10 Mo</p>
              <button type="button" className="idz-btn">Choisir une image</button>
            </div>
          ) : (
            <div className="dep-preview-wrap">
              <img src={preview} alt="Preuve M-Pesa" className="dep-preview-img" />
              {!isLoading && (
                <button className="dep-remove-img" onClick={() => { setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value = ''; }}>
                  ✕ Changer
                </button>
              )}
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
            hidden
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>

        {/* Error */}
        {(errorMsg || step === 'error') && (
          <div className="dep-error">⚠️ {errorMsg}</div>
        )}

        {/* Loading state label */}
        {step === 'uploading'   && <p className="dep-loading">📤 Upload de l'image en cours...</p>}
        {step === 'submitting'  && <p className="dep-loading">⏳ Création du dépôt en cours...</p>}

        <button
          className={`wha-btn primary dep-submit-btn ${isLoading ? 'loading' : ''}`}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading
            ? <><span className="betslip-spinner" /> En cours...</>
            : 'Soumettre le dépôt'}
        </button>
      </div>

      {/* How it works */}
      <div className="invoice-info-box">
        <span>💡</span>
        <div>
          <p className="iib-title">Comment ça marche ?</p>
          <ul className="iib-list">
            <li>Effectuez votre virement M-Pesa vers notre numéro</li>
            <li>Entrez le montant et la référence de transaction</li>
            <li>Uploadez votre capture d'écran de confirmation</li>
            <li>Notre équipe valide sous 24h et crédite votre solde</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   WITHDRAWAL TAB — full M-Pesa flow
══════════════════════════════════════════════════ */
function WithdrawalTab({ accessToken, balance, onNeedDeposit }) {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  // Form state
  const [amount, setAmount]       = useState('');
  const [mpesa, setMpesa]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const loadWithdrawals = useCallback(() => {
    if (!accessToken) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/v1/withdrawals`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then((data) => { setWithdrawals(Array.isArray(data) ? data : []); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [accessToken]);

  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);

  const pendingWithdrawal = withdrawals.find((w) => w.status === 'pending');
  const history           = withdrawals.filter((w) => w.status !== 'pending');
  const hasPending        = !!pendingWithdrawal;

  const handleSubmit = async () => {
    setFormError('');
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setFormError('Le montant doit être supérieur à 0.'); return; }
    if (amt > parseFloat(balance)) {
      setFormError('Solde insuffisant.');
      setTimeout(() => onNeedDeposit?.(), 1500);
      return;
    }
    if (!mpesa.trim()) { setFormError('Le numéro M-Pesa est obligatoire.'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/withdrawals`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ amount: amt, mpesa_number: mpesa.trim() }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || `Erreur ${res.status}`;
        if (msg.toLowerCase().includes('pending') || msg.toLowerCase().includes('attente')) {
          setFormError("Vous avez déjà une demande en cours. Attendez qu'elle soit traitée.");
        } else {
          setFormError(msg);
        }
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      setAmount('');
      setMpesa('');
      loadWithdrawals(); // refresh list to show the new pending item
    } catch (e) {
      setFormError(e.message || 'Erreur réseau.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wallet-section">

      {/* ── Pending withdrawal banner ── */}
      {hasPending && (
        <div className="wd-pending-banner">
          <span className="wd-pending-icon">⏳</span>
          <div>
            <p className="wd-pending-title">Retrait en cours de traitement</p>
            <p className="wd-pending-desc">
              Votre demande de <strong>{parseFloat(pendingWithdrawal.amount).toFixed(2)} FC</strong> vers{' '}
              <strong>{pendingWithdrawal.mpesa_number}</strong> est en attente.
              L'admin va traiter votre retrait.
            </p>
          </div>
        </div>
      )}

      {/* ── Form ── */}
      <div className={`wallet-card ${hasPending ? 'wd-form-disabled' : ''}`}>
        <h3 className="wc-title">Demande de retrait</h3>
        <p className="wc-desc">
          {hasPending
            ? 'Vous ne pouvez soumettre qu\'une seule demande à la fois.'
            : 'Les fonds seront envoyés sur votre numéro M-Pesa après validation.'}
        </p>

        <div className="dep-field">
          <label className="dep-label">Montant à retirer (€)</label>
          <input
            type="number"
            className="dep-input"
            placeholder="Ex : 50"
            min="1"
            max={balance}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={hasPending || submitting}
          />
          <p className="dep-hint">Solde disponible : <strong>{parseFloat(balance).toFixed(2)} FC</strong></p>
        </div>

        <div className="dep-field">
          <label className="dep-label">Numéro M-Pesa</label>
          <input
            type="tel"
            className="dep-input"
            placeholder="Ex : 0712345678"
            value={mpesa}
            onChange={(e) => setMpesa(e.target.value)}
            disabled={hasPending || submitting}
          />
        </div>

        {formError && <div className="dep-error">⚠️ {formError}</div>}

        {submitted && !hasPending && (
          <div className="wd-success-msg">
            ✅ Demande envoyée — l'admin va traiter votre retrait.
          </div>
        )}

        <button
          className={`wha-btn primary dep-submit-btn ${submitting ? 'loading' : ''}`}
          onClick={handleSubmit}
          disabled={hasPending || submitting}
        >
          {submitting
            ? <><span className="betslip-spinner" /> En cours...</>
            : hasPending ? 'Demande en cours' : 'Soumettre le retrait'}
        </button>
      </div>

      {/* ── History ── */}
      {!loading && !error && history.length > 0 && (
        <div className="wallet-card">
          <div className="wc-header">
            <h3 className="wc-title">Historique des retraits</h3>
            <button className="wc-see-all" onClick={loadWithdrawals}>🔄</button>
          </div>
          <div className="deposits-list">
            {history.map((w) => (
              <div key={w.id} className={`deposit-row deposit-row-${w.status === 'paid' ? 'approved' : 'pending'} wd-history-row`}>
                {/* Top row: icon + info + amount */}
                <div className="wd-row-top">
                  <div className="deposit-row-left">
                    <div className="deposit-row-icon">📤</div>
                    <div>
                      <p className="deposit-row-ref">{w.mpesa_number}</p>
                      <p className="deposit-row-date">{formatDate(w.created_at)}</p>
                    </div>
                  </div>
                  <div className="deposit-row-right">
                    <p className="deposit-row-amount" style={{ color: '#e53935' }}>
                      -{parseFloat(w.amount).toFixed(2)} FC
                    </p>
                    <span className={`deposit-status-badge ${w.status === 'paid' ? 'status-approved' : 'status-pending'}`}>
                      {w.status === 'paid' ? '✓ Payé' : '⏳ En attente'}
                    </span>
                  </div>
                </div>

                {/* Paid details — reference, note, proof image */}
                {w.status === 'paid' && (
                  <div className="wd-paid-details">
                    {w.admin_reference && (
                      <p className="wd-paid-ref">
                        🔑 <strong>Référence M-Pesa :</strong> {w.admin_reference}
                      </p>
                    )}
                    {w.admin_note && (
                      <p className="wd-paid-note">💬 {w.admin_note}</p>
                    )}
                    {w.admin_proof_url && (
                      <div className="wd-proof-wrap">
                        <p className="wd-proof-label">📎 Preuve de paiement</p>
                        <img
                          src={`${API_BASE_URL}${w.admin_proof_url}`}
                          alt="Preuve de paiement"
                          className="wd-proof-img"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="dep-loading"><span className="betslip-spinner" /> Chargement...</div>}
      {error   && <div className="dep-error">⚠️ {error}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   DEPOSITS HISTORY TAB
══════════════════════════════════════════════════ */
function DepositsTab({ accessToken }) {
  const [deposits, setDeposits]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const load = useCallback(() => {
    if (!accessToken) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/v1/deposits`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then((data) => { setDeposits(Array.isArray(data) ? data : []); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="wallet-section">
      <div className="wallet-card">
        <div className="wc-header">
          <h3 className="wc-title">Historique des dépôts</h3>
          <button className="wc-see-all" onClick={load}>🔄 Actualiser</button>
        </div>

        {loading && <div className="dep-loading"><span className="betslip-spinner" /> Chargement...</div>}
        {error   && <div className="dep-error">⚠️ {error}</div>}

        {!loading && !error && deposits.length === 0 && (
          <p className="history-empty">Aucun dépôt pour le moment.</p>
        )}

        {!loading && !error && deposits.length > 0 && (
          <div className="deposits-list">
            {deposits.map((dep) => (
              <div key={dep.id} className={`deposit-row deposit-row-${dep.status}`}>
                <div className="deposit-row-left">
                  <div className="deposit-row-icon">📥</div>
                  <div>
                    <p className="deposit-row-ref">{dep.reference}</p>
                    <p className="deposit-row-date">{formatDate(dep.created_at)}</p>
                    {dep.admin_comment && dep.status === 'rejected' && (
                      <p className="deposit-row-comment">💬 {dep.admin_comment}</p>
                    )}
                  </div>
                </div>
                <div className="deposit-row-right">
                  <p className="deposit-row-amount">+{parseFloat(dep.amount).toFixed(2)} FC</p>
                  <StatusBadge status={dep.status} />
                  {dep.proof_image && (
                    <a
                      href={`${API_BASE_URL}${dep.proof_image}`}
                      target="_blank"
                      rel="noreferrer"
                      className="deposit-row-proof"
                    >
                      📎 Preuve
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN WALLET PAGE
══════════════════════════════════════════════════ */
export default function WalletPage() {
  const { balance, accessToken, fetchWallet } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  // Verification state (local — no API yet)
  const [docs, setDocs]               = useState({ identity: null, address: null, payment: null });
  const [docPreviews, setDocPreviews] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const fileRefs = { identity: useRef(null) };

  const handleDocUpload = (type, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocs((d) => ({ ...d, [type]: file }));
    setDocPreviews((p) => ({ ...p, [type]: URL.createObjectURL(file) }));
    setUploadStatus((s) => ({ ...s, [type]: 'uploading' }));
    setTimeout(() => setUploadStatus((s) => ({ ...s, [type]: 'done' })), 2000);
  };

  const removeDoc = (type) => {
    setDocs((d) => ({ ...d, [type]: null }));
    setDocPreviews((p) => ({ ...p, [type]: null }));
    setUploadStatus((s) => ({ ...s, [type]: null }));
    if (fileRefs[type].current) fileRefs[type].current.value = '';
  };

  const allVerified = Object.values(uploadStatus).filter((s) => s === 'done').length;

  // After a successful deposit refresh balance
  const handleDepositSuccess = () => {
    fetchWallet();
    setTimeout(() => setActiveTab(3), 1500); // switch to history after 1.5s
  };

  return (
    <div className="wallet-page">

      {/* ── Balance hero ── */}
      <div className="wallet-hero">
        <div className="wallet-hero-left">
          <p className="wallet-hero-label">Solde disponible</p>
          <h1 className="wallet-hero-balance">{Number(balance).toLocaleString('fr-FR')} FC</h1>
          <p className="wallet-hero-sub">Mis à jour en temps réel</p>
        </div>
        <div className="wallet-hero-actions">
          <button className="wha-btn primary"   onClick={() => setActiveTab(1)}>+ Déposer</button>
          <button className="wha-btn secondary" onClick={() => setActiveTab(2)}>↑ Retirer</button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="wallet-tabs">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            className={`wallet-tab ${activeTab === i ? 'active' : ''}`}
            onClick={() => setActiveTab(i)}
          >
            {i === 4 && allVerified < 1 && <span className="tab-dot" />}
            {tab}
          </button>
        ))}
      </div>

      {/* ── Tab 0: Overview ── */}
      {activeTab === 0 && (
        <div className="wallet-section">
          <div className="wallet-quick-actions">
            <button className="wqa-card" onClick={() => setActiveTab(1)}>
              <span className="wqa-icon">📥</span>
              <p className="wqa-label">Effectuer un dépôt</p>
              <p className="wqa-desc">Via M-Pesa</p>
            </button>
            <button className="wqa-card" onClick={() => setActiveTab(2)}>
              <span className="wqa-icon">📤</span>
              <p className="wqa-label">Demander un retrait</p>
              <p className="wqa-desc">Envoyez votre facture</p>
            </button>
            <button className="wqa-card" onClick={() => setActiveTab(3)}>
              <span className="wqa-icon">📋</span>
              <p className="wqa-label">Historique dépôts</p>
              <p className="wqa-desc">Voir vos dépôts</p>
            </button>
          </div>
        </div>
      )}

      {/* ── Tab 1: Deposit ── */}
      {activeTab === 1 && (
        <DepositTab
          accessToken={accessToken}
          onDepositSuccess={handleDepositSuccess}
        />
      )}

      {/* ── Tab 2: Withdrawal ── */}
      {activeTab === 2 && (
        <WithdrawalTab
          accessToken={accessToken}
          balance={balance}
          onNeedDeposit={() => setActiveTab(1)}
        />
      )}

      {/* ── Tab 3: Deposits history ── */}
      {activeTab === 3 && (
        <DepositsTab accessToken={accessToken} />
      )}

      {/* ── Tab 4: Verification ── */}
      {activeTab === 4 && (
        <div className="wallet-section">
          <div className="verify-progress-card">
            <div className="vpc-left">
              <h3>Vérification du compte</h3>
              <p>Envoyez vos documents pour sécuriser votre compte.</p>
            </div>
            <div className="vpc-right">
              <div className="vpc-circle">
                <svg viewBox="0 0 36 36">
                  <path className="vpc-bg"   d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className="vpc-fill" strokeDasharray={`${allVerified * 100}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span className="vpc-pct">{allVerified}/1</span>
              </div>
              <p className="vpc-label">{allVerified === 1 ? '✓ Vérifié' : 'Document envoyé'}</p>
            </div>
          </div>

          {DOC_TYPES.map(({ id, label, icon, desc }) => {
            const status  = uploadStatus[id];
            const preview = docPreviews[id];
            const isImage = docs[id]?.type?.startsWith('image/');
            return (
              <div key={id} className={`doc-card ${status === 'done' ? 'verified' : status === 'uploading' ? 'uploading' : ''}`}>
                <div className="doc-card-left">
                  <div className="doc-icon">{icon}</div>
                  <div>
                    <p className="doc-title">{label}</p>
                    <p className="doc-desc">{desc}</p>
                  </div>
                </div>
                <div className="doc-card-right">
                  {status === 'done'      && <div className="doc-status done">✓ Reçu</div>}
                  {status === 'uploading' && <div className="doc-status uploading">⏳ Traitement...</div>}
                  {!status                && <div className="doc-status pending">En attente</div>}
                  {preview && isImage && <div className="doc-preview"><img src={preview} alt={label} /></div>}
                  <div className="doc-actions">
                    {!status && <button className="doc-upload-btn" onClick={() => fileRefs[id].current?.click()}>📎 Choisir</button>}
                    {status === 'done' && <button className="doc-remove-btn" onClick={() => removeDoc(id)}>Remplacer</button>}
                  </div>
                  <input ref={fileRefs[id]} type="file" accept="image/*,.pdf" hidden onChange={(e) => handleDocUpload(id, e)} />
                </div>
              </div>
            );
          })}

          {allVerified === 1 && (
            <div className="all-verified-banner">
              <span className="avb-icon">🎉</span>
              <div>
                <p className="avb-title">Compte entièrement vérifié !</p>
                <p className="avb-desc">Vous pouvez maintenant déposer et retirer sans limites.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
